import { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScanInput } from "@/components/BarcodeScanner";
import { toast } from "sonner";
import { Upload, UserPlus, BatteryCharging } from "lucide-react";

const PAYMENT_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday"];
const EMPTY = { name: "", mobile: "", address: "", area: "", payment_day: "Monday",
  onboarded_date: "", last_payment_date: "", vehicle_id: "", battery_serial: "",
  deposit_amount: "", deposit_status: "Pending" };

function RiderForm({ mode, vehicles, onDone }) {
  const [f, setF] = useState({ ...EMPTY });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const existing = mode === "existing";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...f, rider_type: mode,
        vehicle_id: f.vehicle_id || null, battery_serial: f.battery_serial || null,
        deposit_amount: parseFloat(f.deposit_amount) || 0, deposit_status: f.deposit_status,
        onboarded_date: existing ? f.onboarded_date : null,
        last_payment_date: existing && f.last_payment_date ? f.last_payment_date : null };
      const { data } = await api.post("/riders", payload);
      if (data.asset_errors?.length) toast.warning(`Rider created, but: ${data.asset_errors.join("; ")}`);
      else toast.success(`Rider ${data.rider_id} onboarded`);
      setF({ ...EMPTY });
      onDone?.();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="card-tactical p-5 space-y-4" data-testid={`${mode}-rider-form`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Full Name *</label>
          <Input data-testid="rider-name-input" required value={f.name} onChange={set("name")} className="bg-slate-50 border-border" />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Mobile Number *</label>
          <Input data-testid="rider-mobile-input" required value={f.mobile} onChange={set("mobile")} className="bg-slate-50 border-border font-mono" />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Address</label>
          <Textarea data-testid="rider-address-input" value={f.address} onChange={set("address")} rows={2} className="bg-slate-50 border-border" />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Area / Zone</label>
          <Input data-testid="rider-area-input" value={f.area} onChange={set("area")} placeholder="e.g. North" className="bg-slate-50 border-border" />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Payment Day *</label>
          <Select value={f.payment_day} onValueChange={set("payment_day")}>
            <SelectTrigger className="bg-slate-50 border-border" data-testid="rider-payment-day"><SelectValue /></SelectTrigger>
            <SelectContent>{PAYMENT_DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Security Deposit (₹)</label>
          <Input data-testid="rider-deposit-amount" type="number" min="0" value={f.deposit_amount}
            onChange={set("deposit_amount")} placeholder="e.g. 1500" className="bg-slate-50 border-border font-mono" />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Deposit Status</label>
          <Select value={f.deposit_status} onValueChange={set("deposit_status")}>
            <SelectTrigger className="bg-slate-50 border-border" data-testid="rider-deposit-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Paid">Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {existing && (
          <>
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Actual Onboarding Date *</label>
              <Input data-testid="rider-onboarded-date" type="date" required value={f.onboarded_date} onChange={set("onboarded_date")} className="bg-slate-50 border-border" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Last Payment Made On (optional)</label>
              <Input data-testid="rider-last-payment-date" type="date" value={f.last_payment_date} onChange={set("last_payment_date")} className="bg-slate-50 border-border" />
            </div>
          </>
        )}
        <div className="space-y-1.5">
          <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Assign Vehicle</label>
          <Select value={f.vehicle_id} onValueChange={set("vehicle_id")}>
            <SelectTrigger className="bg-slate-50 border-border" data-testid="rider-vehicle-select"><SelectValue placeholder="Select available vehicle" /></SelectTrigger>
            <SelectContent>
              {vehicles.map((v) => (
                <SelectItem key={v.vehicle_id} value={v.vehicle_id}>
                  {v.vehicle_id} · {v.registration_number || v.chassis_last4}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {vehicles.length === 0 && <p className="text-xs text-amber-400">No unassigned vehicles available.</p>}
        </div>
        <ScanInput label="Assign Battery (scan barcode/QR)" value={f.battery_serial}
          onChange={set("battery_serial")} testid="rider-battery-serial" />
      </div>
      <Button type="submit" disabled={busy} data-testid={`${mode}-rider-submit`}
        className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold">
        <UserPlus className="h-4 w-4 mr-2" /> {busy ? "Saving…" : existing ? "Add Existing Rider" : "Onboard Rider"}
      </Button>
    </form>
  );
}

function BulkImport({ onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/riders/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.created} riders imported`);
      onDone?.();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="card-tactical p-5 space-y-4" data-testid="bulk-import-form">
      <p className="text-sm text-slate-400">
        Upload a <span className="font-mono text-slate-600">.csv</span> or <span className="font-mono text-slate-600">.xlsx</span> file with columns:
      </p>
      <code className="block text-xs bg-slate-50 border border-border rounded-lg p-3 text-emerald-300 overflow-x-auto">
        name, mobile, address, area, payment_day, onboarded_date, last_payment_date, vehicle_id, battery_serial, deposit_amount
      </code>
      <p className="text-xs text-slate-500">payment_day: Monday–Thursday · dates: DD/MM/YYYY or YYYY-MM-DD · vehicle_id, battery_serial & deposit_amount optional</p>
      <Input data-testid="bulk-file-input" type="file" accept=".csv,.xlsx,.xls"
        onChange={(e) => setFile(e.target.files?.[0] || null)} className="bg-slate-50 border-border" />
      <Button type="submit" disabled={!file || busy} data-testid="bulk-import-submit"
        className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold">
        <Upload className="h-4 w-4 mr-2" /> {busy ? "Importing…" : "Import Riders"}
      </Button>
      {result && (
        <div className="space-y-2" data-testid="bulk-import-result">
          <p className="text-sm text-emerald-400 font-medium">{result.created} riders created.</p>
          {result.errors.length > 0 && (
            <ul className="text-xs text-amber-400 space-y-1 max-h-40 overflow-y-auto">
              {result.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
        </div>
      )}
    </form>
  );
}

function IssueAssets({ riders, vehicles, onDone }) {
  const [riderOid, setRiderOid] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [serial, setSerial] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/riders/${riderOid}/assign`, { vehicle_id: vehicleId || null, battery_serial: serial || null });
      toast.success("Vehicle / battery issued");
      setRiderOid(""); setVehicleId(""); setSerial("");
      onDone?.();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="card-tactical p-5 space-y-4" data-testid="issue-assets-form">
      <p className="text-sm text-slate-400">Issue or replace a vehicle and/or battery for an existing rider. Any currently assigned assets are released automatically.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5 sm:col-span-2">
          <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Rider *</label>
          <Select value={riderOid} onValueChange={setRiderOid}>
            <SelectTrigger className="bg-slate-50 border-border" data-testid="issue-rider-select"><SelectValue placeholder="Select rider" /></SelectTrigger>
            <SelectContent>
              {riders.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} · {r.mobile} · {r.rider_id}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Vehicle</label>
          <Select value={vehicleId} onValueChange={setVehicleId}>
            <SelectTrigger className="bg-slate-50 border-border" data-testid="issue-vehicle-select"><SelectValue placeholder="Select available vehicle" /></SelectTrigger>
            <SelectContent>
              {vehicles.map((v) => <SelectItem key={v.vehicle_id} value={v.vehicle_id}>{v.vehicle_id} · {v.registration_number || v.chassis_last4}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <ScanInput label="Battery (scan barcode/QR)" value={serial} onChange={setSerial} testid="issue-battery-serial" />
      </div>
      <Button type="submit" disabled={!riderOid || (!vehicleId && !serial) || busy} data-testid="issue-assets-submit"
        className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold">
        <BatteryCharging className="h-4 w-4 mr-2" /> {busy ? "Issuing…" : "Issue Vehicle / Battery"}
      </Button>
    </form>
  );
}

export default function Onboarding() {
  const [vehicles, setVehicles] = useState([]);
  const [riders, setRiders] = useState([]);
  const [tab, setTab] = useState("new");

  const load = () => {
    api.get("/vehicles", { params: { status: "Unassigned" } }).then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/riders").then((r) => setRiders(r.data)).catch(() => {});
  };
  useEffect(load, []);

  return (
    <div className="space-y-5" data-testid="onboarding-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Rider Onboarding</h1>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-card border border-border" data-testid="onboarding-tabs">
          <TabsTrigger value="new" data-testid="tab-new-rider">New Rider</TabsTrigger>
          <TabsTrigger value="existing" data-testid="tab-existing-rider">Existing Rider</TabsTrigger>
          <TabsTrigger value="bulk" data-testid="tab-bulk-import">Bulk Import</TabsTrigger>
          <TabsTrigger value="issue" data-testid="tab-issue-assets">Issue Vehicle / Battery</TabsTrigger>
        </TabsList>
        <TabsContent value="new" className="mt-4"><RiderForm mode="new" vehicles={vehicles} onDone={load} /></TabsContent>
        <TabsContent value="existing" className="mt-4"><RiderForm mode="existing" vehicles={vehicles} onDone={load} /></TabsContent>
        <TabsContent value="bulk" className="mt-4"><BulkImport onDone={load} /></TabsContent>
        <TabsContent value="issue" className="mt-4"><IssueAssets riders={riders} vehicles={vehicles} onDone={load} /></TabsContent>
      </Tabs>
    </div>
  );
}
